// Кеш публичных профилей (PublicUser) — для отображения имён/аватаров собеседников в direct-чатах,
// участников групп, авторов в группах, автокомплита упоминаний. Директория — НЕ-стор-данные
// (SPEC §4.0/§2): тянем через transport.rest (getUser/listUsers), кешируем в app-ui (UI-данные).
// Доменный тип PublicUser — из shared-contracts (инв.1).

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react'
import type { PublicUser, UserId } from '@tele-chat/shared-contracts'
import { useRest } from '../transport/TransportContext.js'

export interface UserCacheValue {
  /** Синхронно — из кеша (или undefined). */
  get(id: UserId): PublicUser | undefined
  /** Запросить и закешировать (если ещё нет). Идемпотентно по id (без дублей запросов). */
  ensure(id: UserId): void
  /** Подмешать в кеш (из listUsers/директории/createDirect). */
  prime(users: PublicUser[]): void
}

const UserCacheContext = createContext<UserCacheValue | null>(null)

/**
 * Пауза перед повтором неудачного getUser (404 удалённого пользователя / сеть). Без неё — петля:
 * сбой → ре-рендер → ensure → снова запрос (прод форка «Наш.Чат» 24–26.08.2026: 2446 запросов на удалённый профиль).
 */
export const USER_RETRY_AFTER_MS = 60_000

export interface UserCacheProviderProps {
  children: ReactNode
}

export function UserCacheProvider({ children }: UserCacheProviderProps): JSX.Element {
  const rest = useRest()
  const [version, force] = useState(0)
  const cache = useRef<Map<UserId, PublicUser>>(new Map())
  const inflight = useRef<Set<UserId>>(new Set())
  const failedAt = useRef<Map<UserId, number>>(new Map())

  const rerender = useCallback((): void => force((n) => n + 1), [])

  const get = useCallback((id: UserId): PublicUser | undefined => cache.current.get(id), [])

  const prime = useCallback(
    (users: PublicUser[]): void => {
      let changed = false
      for (const u of users) {
        cache.current.set(u.id, u)
        changed = true
      }
      if (changed) rerender()
    },
    [rerender],
  )

  const ensure = useCallback(
    (id: UserId): void => {
      if (cache.current.has(id) || inflight.current.has(id)) return
      const failed = failedAt.current.get(id)
      if (failed !== undefined && Date.now() - failed < USER_RETRY_AFTER_MS) return
      inflight.current.add(id)
      rest
        .getUser(id)
        .then((user) => {
          inflight.current.delete(id)
          failedAt.current.delete(id)
          cache.current.set(id, user)
          rerender()
        })
        .catch(() => {
          // Профиль недоступен — без записи (UI покажет id/инициалы). Ре-рендер НЕ зовём: для UI ничего
          // не изменилось, а он запустил бы ensure заново — повтор только после паузы.
          inflight.current.delete(id)
          failedAt.current.set(id, Date.now())
        })
    },
    [rest, rerender],
  )

  // version в deps — чтобы context-value менялся при обновлении кеша (иначе потребители не ре-рендерятся).
  const value = useMemo<UserCacheValue>(() => ({ get, ensure, prime }), [get, ensure, prime, version])

  return <UserCacheContext.Provider value={value}>{children}</UserCacheContext.Provider>
}

export function useUserCache(): UserCacheValue {
  const ctx = useContext(UserCacheContext)
  if (ctx === null) {
    throw new Error('useUserCache(): компонент должен быть внутри <UserCacheProvider>')
  }
  return ctx
}
